<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import type { IResponseData } from '~/types'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import type { ICryptoCompany } from '~/components/entity/crypto-company/item'
import { WidgetAboutPageHeader } from '~/components/widget/about-page'
import { WidgetCryptoCompanyServices } from '~/components/widget/crypto-company'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetBranchesList } from '~/components/widget/branches'
import { WidgetRating } from '~/components/widget/rating'
import { WidgetSidebarNews } from '~/components/widget/sidebar-news'
import { WidgetXrubyReferral } from '~/components/widget/xruby-referral'

const route = useRoute()
const { t, locale } = useI18n()
const url = `/crypto-companies/${route.params.slug}`
const { data } = await useApiWith404<IResponseData<ICryptoCompany>>(() => url, {
  key: `crypto-company-page-${route.params.slug}`
})

const cryptoCompany = computed(() => data.value?.data)

const fitType = computed(() => {
  const company = cryptoCompany.value
  if (!company) return ''

  const mainTypeSlug = company.main_type?.slug?.replace(/-/g, '_')

  return t(`page.crypto-companies.slug.types.${mainTypeSlug}`)
})

const cryptoCompanyForSeo = computed(() => {
  const company = cryptoCompany.value
  if (!company) return company

  return {
    ...company,
    title: [fitType.value, company.title].filter(Boolean).join(' ')
  }
})

const config = useRuntimeConfig()
const baseUrl = config.public.siteUrl
useSeoFromEntity(cryptoCompanyForSeo, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('page.crypto-companies.title'), url: '/crypto-companies' },
  {
    title: cryptoCompany.value?.main_type?.title,
    url: `/crypto-companies/type/${cryptoCompany.value?.main_type?.slug}`
  },
  {
    title: cryptoCompany.value?.title || t('page.crypto-companies.slug.default_title'),
    url: cryptoCompany.value?.url
  }
])

const fitOrganization = computed(() => ({
  title: cryptoCompany.value?.title || '',
  logo: cryptoCompany.value?.logo,
  logo_rectangle: cryptoCompany.value?.logo_rectangle,
  url: cryptoCompany.value?.url || '',
  call_center_phone: cryptoCompany.value?.call_center_phone || '',
  description: cryptoCompany.value?.description || '',
  organization: {
    official_title: cryptoCompany.value?.official_title || '',
    url: cryptoCompany.value?.url || ''
  },
  views_count: cryptoCompany.value?.views_count || 0,
  updated_at: cryptoCompany.value?.updated_at || '',
  status: cryptoCompany.value?.status,
  status_comments: cryptoCompany.value?.status_comments,
  main_type_title: fitType.value || undefined
}))
const fitContacts = computed(() => {
  const contacts =
    cryptoCompany.value?.contacts?.map((item) => {
      const [type, rawValue] = Object.entries(item)[0]
      const value = (rawValue as string)?.trim() ?? ''
      return { type, value }
    }) ?? []

  return {
    logo: cryptoCompany.value?.logo ?? '',
    title: cryptoCompany.value?.title ?? '',
    official_title: cryptoCompany.value?.official_title ?? '',
    call_center_phone: contacts.find((el) => el.type === 'mobile_phone')?.value ?? '',
    email: contacts.find((el) => el.type === 'email')?.value ?? '',
    website_url: contacts.find((el) => el.type === 'website_url')?.value ?? '',
    telegram: contacts.find((el) => el.type === 'telegram')?.value ?? '',
    whatsapp: contacts.find((el) => el.type === 'whatsapp')?.value ?? ''
  }
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts v-if="fitContacts" :organization="fitContacts" />
            <WidgetXrubyReferral v-if="cryptoCompany?.id === 158" />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSidebarNews
              v-if="cryptoCompany && cryptoCompany.news_count"
              :heading="$t('page.news.related_news')"
              path="/news"
              variant="white"
              :query-params="{
                per_page: '5',
                'filter[organization_id]': cryptoCompany.id,
                'filter[organization_type]': 'crypto_company'
              }"
            />
          </aside>
        </template>

        <div class="space-y-5" role="main">
          <WidgetAboutPageHeader v-if="cryptoCompany" :product="fitOrganization" type="crypto" />
          <section>
            <ContentBlock
              variant="white"
              :heading-text="$t('common.description')"
              class="space-y-5"
              heading-tag="h2"
              heading-class="h4"
            >
              <Content v-if="cryptoCompany?.description">
                <div v-html="cryptoCompany.description" />
              </Content>
              <WidgetCryptoCompanyServices
                v-if="cryptoCompany?.services"
                :services="cryptoCompany?.services"
              />
            </ContentBlock>
          </section>

          <section>
            <WidgetBranchesList
              v-if="cryptoCompany"
              :organization="cryptoCompany"
              :hide-organization-details="true"
              :hide-pagination="false"
            />
          </section>

          <WidgetRating
            v-if="cryptoCompany && config.public.widgetRatingEnabled"
            :entity="cryptoCompany"
            entity-type="crypto_company"
            :heading-text="
              $t('page.crypto_company.slug.rating_heading', { title: cryptoCompany.title })
            "
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
